import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState('');

  const reference = searchParams.get('reference') || searchParams.get('trxref');

  useEffect(() => {
    if (!reference) {
      setStatus('failed');
      setError('No payment reference provided');
      return;
    }

    const verifyPayment = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/payments/verify/${reference}`);
        if (response.data.status === 'success') {
          setStatus('success');
          setTransaction(response.data.transaction);
        } else {
          setStatus('failed');
          setError(response.data.message || 'Payment verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('failed');
        setError(err.response?.data?.detail || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [reference]);

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card-gold p-8 md:p-12 max-w-lg w-full text-center"
        data-testid="payment-callback"
      >
        {status === 'verifying' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-main/10 flex items-center justify-center">
              <Loader size={40} className="text-primary-main animate-spin" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-text-primary mb-2">
              Verifying Payment...
            </h1>
            <p className="text-text-secondary">Please wait while we confirm your transaction</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent-success/20 flex items-center justify-center">
              <CheckCircle size={48} className="text-accent-success" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">
              Payment Successful!
            </h1>
            <p className="text-text-secondary mb-6">
              Your payment has been confirmed. Your crypto will be sent to your wallet shortly.
            </p>

            {transaction && (
              <div className="bg-background rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Reference:</span>
                    <span className="text-text-primary font-mono">{reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Amount:</span>
                    <span className="text-text-primary font-semibold">
                      ₦{transaction.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Crypto:</span>
                    <span className="text-text-primary font-semibold">{transaction.crypto_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Status:</span>
                    <span className="text-accent-success font-semibold uppercase">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1">
                <button className="btn-gold w-full flex items-center justify-center gap-2" data-testid="back-home-btn">
                  Back to Home
                  <ArrowRight size={16} />
                </button>
              </Link>
              <Link to="/trade" className="flex-1">
                <button className="px-6 py-3 rounded-full bg-background border border-border text-text-primary hover:border-primary-main/50 transition-all w-full">
                  Buy More
                </button>
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent-danger/20 flex items-center justify-center">
              <XCircle size={48} className="text-accent-danger" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">
              Payment Failed
            </h1>
            <p className="text-text-secondary mb-6">
              {error || 'Your payment could not be processed. Please try again.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/trade" className="flex-1">
                <button className="btn-gold w-full" data-testid="try-again-btn">
                  Try Again
                </button>
              </Link>
              <Link to="/contact" className="flex-1">
                <button className="px-6 py-3 rounded-full bg-background border border-border text-text-primary hover:border-primary-main/50 transition-all w-full">
                  Contact Support
                </button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
